import type { $Fetch } from "nitropack";
import type { SpokenLanguage } from "~/help/languages";

export interface Skill {
    skill_id: string;
    name: string;
    description?: string;
    category?: {
        id: string;
        name: string;
        description?: string;
    };
}

export interface MatchingItem {
    id: string;
    name: string;
}

export interface MatchingCriteria {
    skills: MatchingItem[];
    market_segments: MatchingItem[];
    personas: MatchingItem[];
    deal_sizes: MatchingItem[];
    industries: MatchingItem[];
}

export interface ExpertSearchResult {
    id: string;
    object: string;
    handle?: string;
    first_name: string;
    last_name: string;
    title?: string;
    signed_picture_url?: string;
    skills: Skill[];
    validated_feedback_count: number;
    is_bookmarked: boolean;
    experience?: string;
    matching_criteria: MatchingCriteria;
    created_at: string;
    languages: SpokenLanguage[];
}

export interface ExpertSearchResponse {
    object: string;
    data: ExpertSearchResult[];
    total: number;
    offset: number;
    limit: number;
}

export interface CriterionScoreDetail {
    criterion_type: 'deal_size' | 'market_segment' | 'persona' | 'industry' | 'company_size';
    name: string;
    score: number;
    repetitions: number;
}

export interface ScoreBreakdown {
    skills_matched: number;
    total_required_skills: number;
    criteria: CriterionScoreDetail[];
}

export interface ExpertByMissionResult extends ExpertSearchResult {
    match_score: number;
    score_breakdown?: ScoreBreakdown;
}

export interface ExpertByMissionResponse {
    object: string;
    data: ExpertByMissionResult[];
    total: number;
    offset: number;
    limit: number;
}

export interface CompanyMissionItem {
    id: string;
    object: string;
    name?: string;
    description?: string;
    status: string;
    created_at: string;
}

export interface CompanyMissionsResponse {
    object: string;
    data: CompanyMissionItem[];
    total: number;
    offset: number;
    limit: number;
}

export interface SendToExpertsResult {
    expert_user_id: string;
    success: boolean;
    error?: string;
}

export interface SendToExpertsResponse {
    object: string;
    sent_count: number;
    failed_count: number;
    results: SendToExpertsResult[];
}

export interface UserMatchingResponse {
    object: string;
    mission_id: string;
    user_id: string;
    match_score: number;
    score_breakdown?: ScoreBreakdown;
}

export interface ExpertSearchFilters {
    title?: string;
    experience_levels?: string[];
    skill_ids?: string[];
    market_segment_ids?: string[];
    persona_ids?: string[];
    deal_size_ids?: string[];
    industry_ids?: string[];
    languages?: SpokenLanguage[];
    offset?: number;
    limit?: number;
}

export const ExpertRepository = (client: $Fetch) => ({
    search: (filters: ExpertSearchFilters, config?: any): Promise<ExpertSearchResponse> => {
        // Build query params with filter[] prefix
        const params: Record<string, string> = {};

        if (filters.title) {
            params['filter[title]'] = filters.title;
        }
        if (filters.experience_levels && filters.experience_levels.length > 0) {
            params['filter[experience_levels]'] = filters.experience_levels.join(',');
        }
        if (filters.skill_ids && filters.skill_ids.length > 0) {
            params['filter[skill_ids]'] = filters.skill_ids.join(',');
        }
        if (filters.market_segment_ids && filters.market_segment_ids.length > 0) {
            params['filter[market_segment_ids]'] = filters.market_segment_ids.join(',');
        }
        if (filters.persona_ids && filters.persona_ids.length > 0) {
            params['filter[persona_ids]'] = filters.persona_ids.join(',');
        }
        if (filters.deal_size_ids && filters.deal_size_ids.length > 0) {
            params['filter[deal_size_ids]'] = filters.deal_size_ids.join(',');
        }
        if (filters.industry_ids && filters.industry_ids.length > 0) {
            params['filter[industry_ids]'] = filters.industry_ids.join(',');
        }
        if (filters.languages && filters.languages.length > 0) {
            params['filter[languages]'] = filters.languages.join(',');
        }
        if (filters.offset !== undefined) {
            params['offset'] = String(filters.offset);
        }
        if (filters.limit !== undefined) {
            params['limit'] = String(filters.limit);
        }

        return client('/v2/experts/search', {
            ...config,
            params,
        });
    },

    searchByMission: (
        missionId: string,
        options?: { offset?: number; limit?: number; weights?: { deal_size_weight: number; market_segment_weight: number; persona_weight: number; industry_weight: number; company_size_weight: number } },
        config?: any
    ): Promise<ExpertByMissionResponse> => {
        const params: Record<string, string> = {};
        if (options?.offset !== undefined) {
            params['offset'] = String(options.offset);
        }
        if (options?.limit !== undefined) {
            params['limit'] = String(options.limit);
        }
        if (options?.weights) {
            params['deal_size_weight'] = String(options.weights.deal_size_weight);
            params['market_segment_weight'] = String(options.weights.market_segment_weight);
            params['persona_weight'] = String(options.weights.persona_weight);
            params['industry_weight'] = String(options.weights.industry_weight);
            params['company_size_weight'] = String(options.weights.company_size_weight);
        }

        return client(`/v2/experts/search-by-mission/${missionId}`, {
            ...config,
            params,
        });
    },

    sendMissionToExperts: (
        missionId: string,
        expertUserIds: string[],
    ): Promise<SendToExpertsResponse> => {
        return client(`/v2/missions/${missionId}/send-to-experts`, {
            method: 'POST',
            body: { expert_user_ids: expertUserIds },
        });
    },

    getUserMatching: (
        missionId: string,
        userId: string,
        weights?: {
            deal_size_weight?: number;
            market_segment_weight?: number;
            persona_weight?: number;
            industry_weight?: number;
            company_size_weight?: number;
        },
        config?: any
    ): Promise<UserMatchingResponse> => {
        const params: Record<string, string> = {};
        if (weights) {
            if (weights.deal_size_weight !== undefined) params['deal_size_weight'] = String(weights.deal_size_weight);
            if (weights.market_segment_weight !== undefined) params['market_segment_weight'] = String(weights.market_segment_weight);
            if (weights.persona_weight !== undefined) params['persona_weight'] = String(weights.persona_weight);
            if (weights.industry_weight !== undefined) params['industry_weight'] = String(weights.industry_weight);
            if (weights.company_size_weight !== undefined) params['company_size_weight'] = String(weights.company_size_weight);
        }
        return client(`/v2/missions/${missionId}/user-matching/${userId}`, {
            ...config,
            params,
        });
    },

    getCompanyMissions: (
        options?: { status?: string[]; offset?: number; limit?: number },
        config?: any
    ): Promise<CompanyMissionsResponse> => {
        const params: Record<string, string> = {};
        if (options?.status && options.status.length > 0) {
            params['filter[status]'] = options.status.join(',');
        }
        if (options?.offset !== undefined) {
            params['offset'] = String(options.offset);
        }
        if (options?.limit !== undefined) {
            params['limit'] = String(options.limit);
        }

        return client('/v2/missions/company', {
            ...config,
            params,
        });
    },
});
